// src/utils/useDeviceDetection.ts
import { useState, useEffect } from 'react';

interface DeviceInfo {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    screenWidth: number;
    screenHeight: number;
}

const useDeviceDetection = (): DeviceInfo => {
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
        isMobile: false,
        isTablet: false,
        isDesktop: false,
        screenWidth: 0,
        screenHeight: 0,
    });

    useEffect(() => {
        const detectDevice = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const userAgent = navigator.userAgent.toLowerCase();

            // Check for mobile devices
            const isMobileDevice = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

            // Check for tablets (including iPad)
            const isTabletDevice = /ipad|android(?!.*mobile)|tablet/i.test(userAgent) ||
                (width >= 768 && width <= 1024);

            // Mobile: width < 768px or mobile user agent
            const isMobile = width < 768 || (isMobileDevice && !isTabletDevice);

            // Tablet: width between 768-1024px or tablet user agent
            const isTablet = (width >= 768 && width <= 1024) || isTabletDevice;

            // Desktop: width > 1024px and not mobile/tablet
            const isDesktop = width > 1024 && !isMobileDevice && !isTabletDevice;

            setDeviceInfo({
                isMobile,
                isTablet,
                isDesktop,
                screenWidth: width,
                screenHeight: height,
            });
        };

        // Initial detection
        detectDevice();

        // Listen for window resize
        window.addEventListener('resize', detectDevice);

        // Listen for orientation change (mobile devices)
        window.addEventListener('orientationchange', () => {
            // Small delay to ensure dimensions are updated after orientation change
            setTimeout(detectDevice, 100);
        });

        // Cleanup event listeners
        return () => {
            window.removeEventListener('resize', detectDevice);
            window.removeEventListener('orientationchange', detectDevice);
        };
    }, []);

    return deviceInfo;
};

export default useDeviceDetection;

// Alternative hook with more specific breakpoints
export const useResponsiveBreakpoints = () => {
    const [breakpoint, setBreakpoint] = useState<string>('');

    useEffect(() => {
        const getBreakpoint = () => {
            const width = window.innerWidth;

            if (width < 576) return 'xs'; // Extra small devices
            if (width < 768) return 'sm'; // Small devices
            if (width < 992) return 'md'; // Medium devices (tablets)
            if (width < 1200) return 'lg'; // Large devices
            return 'xl'; // Extra large devices
        };

        const handleResize = () => {
            setBreakpoint(getBreakpoint());
        };

        // Initial set
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return {
        breakpoint,
        isXs: breakpoint === 'xs',
        isSm: breakpoint === 'sm',
        isMd: breakpoint === 'md',
        isLg: breakpoint === 'lg',
        isXl: breakpoint === 'xl',
        isMobile: breakpoint === 'xs' || breakpoint === 'sm',
        isTablet: breakpoint === 'md',
        isDesktop: breakpoint === 'lg' || breakpoint === 'xl',
    };
};
